import { useState, useEffect } from 'react';
import { useTags } from '../../tags/tags-provider';

interface UseLeafEditorProps<T> {
  output: T;
  onUpdate: (newOutput: T) => void;
  isOpen: boolean;
  onClose: () => void;
}

export const useLeafEditor = <T extends Record<string, any>>({
  output,
  onUpdate,
  isOpen,
  onClose
}: UseLeafEditorProps<T>) => {
  const { addTag } = useTags();
  const [editingOutput, setEditingOutput] = useState<T>({ ...output });
  const [newTagName, setNewTagName] = useState('');
  const [newTagDescription, setNewTagDescription] = useState('');
  const [isAddingNewTag, setIsAddingNewTag] = useState(false);
  const [addingProduct, setAddingProduct] = useState(false);

  // Update internal state when output prop changes
  useEffect(() => {
    setEditingOutput({ ...output });
  }, [output]);

  const handleSave = () => {
    onUpdate(editingOutput);
    onClose();
  };

  const handleAddTag = async (isProduct: boolean = false) => {
    if (!isAddingNewTag || !newTagName.trim()) return;
    
    try {
      const newTag = await addTag({
        displayName: newTagName.trim(),
        description: newTagDescription.trim() || undefined,
        isProduct: isProduct
      });
      
      // Reset form state
      setNewTagName('');
      setNewTagDescription('');
      setIsAddingNewTag(false);
      setAddingProduct(false);
      
      return newTag;
    } catch (error) {
      console.error('Failed to add tag:', error);
      return null;
    }
  };

  const handleCancelTagCreation = () => {
    setIsAddingNewTag(false);
    setAddingProduct(false);
    setNewTagName('');
    setNewTagDescription('');
  };

  const handleReorder = (arrayKey: keyof T, fromIndex: number, toIndex: number) => {
    const array = editingOutput[arrayKey] as string[];
    if (!Array.isArray(array)) return;
    
    const reorderedItems = [...array];
    const [movedItem] = reorderedItems.splice(fromIndex, 1);
    reorderedItems.splice(toIndex, 0, movedItem);
    
    setEditingOutput(prev => ({
      ...prev,
      [arrayKey]: reorderedItems
    }));
  };

  const handleRemoveItem = (arrayKey: keyof T, itemId: string) => {
    const array = editingOutput[arrayKey] as string[];
    if (!Array.isArray(array)) return;
    
    setEditingOutput(prev => ({
      ...prev,
      [arrayKey]: array.filter(id => id !== itemId)
    }));
  };

  const handleAddItem = (arrayKey: keyof T, itemId: string) => {
    const array = editingOutput[arrayKey] as string[];
    if (!Array.isArray(array)) return;
    
    setEditingOutput(prev => ({
      ...prev,
      [arrayKey]: [...array, itemId]
    }));
  };

  const handleSelectExisting = (arrayKey: keyof T, value: string) => {
    if (value === 'create-new') {
      setIsAddingNewTag(true);
      setAddingProduct(false);
    } else {
      handleAddItem(arrayKey, value);
    }
  };

  const handleSelectProduct = (arrayKey: keyof T, value: string) => {
    if (value === 'create-new') {
      setIsAddingNewTag(true);
      setAddingProduct(true);
    } else {
      handleAddItem(arrayKey, value);
    }
  };

  return {
    editingOutput,
    setEditingOutput,
    newTagName,
    setNewTagName,
    newTagDescription,
    setNewTagDescription,
    isAddingNewTag,
    setIsAddingNewTag,
    addingProduct,
    setAddingProduct,
    handleSave,
    handleAddTag,
    handleCancelTagCreation,
    handleReorder,
    handleRemoveItem,
    handleAddItem,
    handleSelectExisting,
    handleSelectProduct,
  };
};
